import React, { useCallback, useEffect, useMemo, useState } from "react";
import { axiosRequest } from "../../utility/axios";

const API_URL = process.env.REACT_APP_API_URL;

// Mirrors dao/leaderboardEntry.js TABLE_MULTIPLIERS — display only, points are computed server-side.
const TABLE_MULTIPLIERS = { "100/200": 1, "100/200/500": 1.25, "200/500/1000": 1.5, "500/1000": 1.75, "500/1000/2000": 2 };

// Mirrors display.js PRIZE_TABLE — kept in sync with the promo poster's prize tiers.
const PRIZE_TABLE = [
  { from: 1, to: 1, prize: 250000 },
  { from: 2, to: 2, prize: 120000 },
  { from: 3, to: 3, prize: 80000 },
  { from: 4, to: 5, prize: 50000 },
  { from: 6, to: 10, prize: 30000 },
  { from: 11, to: 15, prize: 10000 },
  { from: 16, to: 17, prize: 7500 },
  { from: 18, to: 20, prize: 5000 },
  { from: 21, to: 30, prize: 2000 },
];
// Milestone leaderboard (Aug 8-20): a player's prize is the highest tier *they've*
// reached, not their rank vs other players — current_tier/next_tier/crowned all come
// straight from the API response, computed server-side (dao/milestoneLeaderboard.js).
const EARLY_SLOT_BONUS = 1.2; // extra multiplier on minutes played 6:00-11:59:59 AM IST

const VARIANTS = {
  contest: {
    title: "Check Your Rank",
    rankingsUrl: "/leaderboard/rankings/public",
    profileUrl: "/leaderboard/player-profile",
    emptyMsg: "No qualifying sessions yet in the contest window.",
  },
  daily: {
    title: "Check Your Daily Rank",
    rankingsUrl: "/leaderboard/rankings/daily/public",
    profileUrl: "/leaderboard/daily-player-profile",
    emptyMsg: "No qualifying sessions yet today.",
  },
  weekend: {
    title: "Check Your Weekend Rank",
    rankingsUrl: "/leaderboard/rankings/weekend/public",
    profileUrl: "/leaderboard/weekend-player-profile",
    emptyMsg: "No qualifying sessions yet this weekend.",
  },
  milestone: {
    title: "Check Your Milestone Progress",
    rankingsUrl: "/leaderboard/rankings/milestone/public",
    profileUrl: "/leaderboard/milestone-player-profile",
    emptyMsg: "No qualifying sessions yet in the milestone window.",
  },
};

// tiedWith is the size of the rank's tie group (1 = no tie) — a cash prize splits evenly
// across everyone sharing that rank; the daily iPhone has no cash value to divide, so ties
// there are only flagged, not split.
const isIphoneVariant = (variant) => variant === "daily" || variant === "weekend";

const prizeForRank = (rank, variant, tiedWith = 1) => {
  if (isIphoneVariant(variant)) return rank === 1 ? "iPhone 17" : null;
  const t = PRIZE_TABLE.find((t) => rank >= t.from && rank <= t.to);
  if (!t) return null;
  return tiedWith > 1 ? Math.round(t.prize / tiedWith) : t.prize;
};

const fmtINR = (n) => Number(n).toLocaleString("en-IN");
const fmtPrize = (p) => (typeof p === "number" ? `${fmtINR(p)}` : p);
const fmtPts = (p, decimals = 1) => {
  const mult = 10 ** decimals;
  const n = Math.round((p || 0) * mult) / mult;
  return n.toLocaleString("en-IN", { minimumFractionDigits: n % 1 === 0 ? 0 : decimals, maximumFractionDigits: decimals });
};
// Two players with genuinely different points can still round to the same 1-decimal
// display value, which reads as a tie even though their real ranks differ. Bump to 2
// decimals only for entries actually involved in such a collision — a true tie (same
// rank) still displays identically.
const computeDisplayDecimals = (rankings) => {
  const decimalsById = {};
  rankings.forEach((cur, i) => {
    let dec = 1;
    for (const n of [rankings[i - 1], rankings[i + 1]]) {
      if (n && n.rank !== cur.rank && Math.round(n.total_points * 10) === Math.round(cur.total_points * 10)) {
        dec = 2;
        break;
      }
    }
    decimalsById[cur._id] = dec;
  });
  return decimalsById;
};
const fmtHrs = (mins) => `${(Math.max(0, mins || 0) / 60).toFixed(1)}h`;
const fmtHoursNeeded = (h) => (h == null ? "" : h < 1 ? `${Math.round(h * 60)}m` : `${h.toFixed(1)}h`);
const IST_TZ = "Asia/Kolkata";
const fmtDateTime = (d) => new Date(d).toLocaleString("en-IN", { timeZone: IST_TZ, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const todayIST = () => {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: IST_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};
const fmtDateShort = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-IN", { timeZone: "UTC", day: "2-digit", month: "short" });
};
// A session can start before the board's period (e.g. before today's midnight IST) or
// still be running past it — clip the window to the period so the displayed time/points
// match what actually counted on the leaderboard, not the session's full raw duration.
const clippedSessionWindow = (s, period) => {
  const rawStart = new Date(s.in_time);
  const rawEnd = s.out_time ? new Date(s.out_time) : new Date();
  if (!period) return { start: rawStart, end: rawEnd };
  const start = new Date(Math.max(rawStart.getTime(), new Date(period.start).getTime()));
  const end = new Date(Math.min(rawEnd.getTime(), new Date(period.end).getTime()));
  return { start, end };
};
const sessionMinutes = (s, period) => {
  const { start, end } = clippedSessionWindow(s, period);
  return Math.max(0, (end - start) / 60000 - (s.break_minutes || 0));
};
const sessionPoints = (s, period) => sessionMinutes(s, period) * (TABLE_MULTIPLIERS[s.table_value] || 1);

// Milestone-only: splits [start, end) into per-day-IST early-slot (6:00-11:59:59 AM) vs
// normal minutes. Mirrors dao/milestoneLeaderboard.js's splitEarlySlot server-side.
const IST_OFFSET_MIN = 5 * 60 + 30;
const splitEarlySlotMinutes = (start, end) => {
  let earlyMs = 0;
  let cursor = start.getTime();
  const endMs = end.getTime();
  while (cursor < endMs) {
    const istCursor = new Date(cursor + IST_OFFSET_MIN * 60000);
    const dayStartUTC = Date.UTC(istCursor.getUTCFullYear(), istCursor.getUTCMonth(), istCursor.getUTCDate(), 0, 0, 0, 0) - IST_OFFSET_MIN * 60000;
    const earlyWindowStart = dayStartUTC + 6 * 60 * 60000;
    const earlyWindowEnd = dayStartUTC + 12 * 60 * 60000;
    const dayEndUTC = dayStartUTC + 24 * 60 * 60000;
    const chunkEnd = Math.min(endMs, dayEndUTC);
    const overlapStart = Math.max(cursor, earlyWindowStart);
    const overlapEnd = Math.min(chunkEnd, earlyWindowEnd);
    if (overlapEnd > overlapStart) earlyMs += overlapEnd - overlapStart;
    cursor = chunkEnd;
  }
  const totalMinutes = Math.max(0, (endMs - start.getTime()) / 60000);
  const earlyMinutes = earlyMs / 60000;
  return { earlyMinutes, normalMinutes: Math.max(0, totalMinutes - earlyMinutes) };
};
const sessionPointsMilestone = (s, period) => {
  const { start, end } = clippedSessionWindow(s, period);
  const totalRawMinutes = Math.max(0, (end - start) / 60000);
  if (totalRawMinutes <= 0) return 0;
  const breakMinutes = Math.min(totalRawMinutes, s.break_minutes || 0);
  const { earlyMinutes: rawEarly, normalMinutes: rawNormal } = splitEarlySlotMinutes(start, end);
  // Deduct break time from normal minutes first, same simplification the backend uses.
  const normalMinutes = Math.max(0, rawNormal - Math.min(rawNormal, breakMinutes));
  const earlyMinutes = Math.max(0, rawEarly - Math.min(rawEarly, Math.max(0, breakMinutes - rawNormal)));
  const multiplier = TABLE_MULTIPLIERS[s.table_value] || 1;
  return normalMinutes * multiplier + earlyMinutes * multiplier * EARLY_SLOT_BONUS;
};
const medalFor = (rank) => (rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null);

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Cinzel:wght@500;700;900&display=swap');
*,*::before,*::after{box-sizing:border-box;}
.pl-root{min-height:100vh;min-height:100dvh;background:radial-gradient(ellipse at 30% 0%,#1a1408 0%,#050403 55%,#000 100%);
  color:#fff;font-family:'Cinzel','Georgia',serif;padding:clamp(18px,4vw,40px) 16px 60px;}
.pl-header{text-align:center;margin-bottom:26px;}
.pl-club{font-size:11px;letter-spacing:5px;color:rgba(255,215,0,.65);text-transform:uppercase;}
.pl-title{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:clamp(22px,6vw,34px);
  background:linear-gradient(90deg,#9a6f00,#FFD700,#FFEC8B,#FFA500,#FFD700,#9a6f00);background-size:300%;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:pl-shimmer 4s linear infinite;letter-spacing:.5px;margin-top:4px;}
@keyframes pl-shimmer{0%{background-position:0% center;}100%{background-position:300% center;}}
.pl-sub{font-size:12.5px;color:rgba(255,255,255,.5);margin-top:6px;letter-spacing:.5px;}
.pl-search-wrap{max-width:420px;margin:0 auto 22px;}
.pl-search-box{display:flex;gap:8px;background:rgba(255,255,255,.04);border:1px solid rgba(255,215,0,.3);
  border-radius:14px;padding:6px;}
.pl-input{flex:1;background:transparent;border:none;color:#fff;font-size:16px;padding:11px 12px;font-family:inherit;}
.pl-input:focus{outline:none;}
.pl-input::placeholder{color:rgba(255,255,255,.35);}
.pl-btn{background:linear-gradient(135deg,#FFD700,#FFA500);color:#1a1408;border:none;border-radius:10px;
  padding:0 20px;font-weight:800;font-size:14px;cursor:pointer;font-family:inherit;letter-spacing:.3px;}
.pl-btn:disabled{opacity:.55;}
.pl-hint{text-align:center;font-size:11.5px;color:rgba(255,255,255,.35);margin-top:8px;}
.pl-err{max-width:420px;margin:0 auto;text-align:center;color:#ff8a8a;background:rgba(255,60,60,.08);
  border:1px solid rgba(255,60,60,.3);border-radius:12px;padding:14px;font-size:13.5px;}
.pl-result{max-width:520px;margin:0 auto;animation:pl-in .4s ease both;}
@keyframes pl-in{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
.pl-profile{background:linear-gradient(180deg,rgba(30,24,10,.9),rgba(10,8,4,.95));border:1px solid rgba(255,215,0,.35);
  border-radius:20px;padding:22px 20px;text-align:center;box-shadow:0 0 50px rgba(255,215,0,.12),0 14px 40px rgba(0,0,0,.5);
  position:relative;overflow:hidden;}
.pl-profile.live{border-color:rgba(255,80,80,.5);}
.pl-medal{font-size:38px;line-height:1;filter:drop-shadow(0 0 12px rgba(255,215,0,.6));}
.pl-rank-plain{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:34px;color:rgba(255,215,0,.85);}
.pl-name{font-weight:800;font-size:19px;margin-top:8px;}
.pl-id{font-size:12px;color:rgba(255,255,255,.45);margin-top:2px;letter-spacing:.5px;}
.pl-live-badge{display:inline-flex;align-items:center;gap:5px;margin-top:10px;padding:3px 12px;border-radius:100px;
  background:rgba(255,60,60,.12);border:1px solid rgba(255,60,60,.4);color:#ff6b6b;font-size:11px;letter-spacing:1px;}
.pl-live-dot{width:6px;height:6px;border-radius:50%;background:#ff3333;box-shadow:0 0 8px #ff3333;animation:pl-blink 1.2s step-start infinite;}
@keyframes pl-blink{0%,100%{opacity:1;}50%{opacity:0;}}
.pl-pts{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;color:#FFD700;font-size:40px;margin-top:14px;
  text-shadow:0 0 18px rgba(255,215,0,.5);}
.pl-pts-label{font-size:10px;color:rgba(255,215,0,.6);letter-spacing:3px;text-transform:uppercase;margin-top:2px;}
.pl-prize{margin-top:10px;font-size:15px;font-weight:700;color:#7be0a0;}
.pl-next-tier{margin-top:8px;font-size:12.5px;font-weight:600;color:rgba(255,255,255,.65);line-height:1.5;}
.pl-next-tier b{color:#FFD700;}
.pl-tip{margin-top:6px;font-size:11px;font-weight:500;color:rgba(126,224,160,.9);line-height:1.4;max-width:280px;margin-left:auto;margin-right:auto;}
.pl-stats{display:flex;justify-content:center;gap:22px;margin-top:16px;padding-top:16px;border-top:1px solid rgba(255,215,0,.15);}
.pl-stat{text-align:center;}
.pl-stat-val{font-weight:800;font-size:16px;color:#fff;}
.pl-stat-label{font-size:10px;color:rgba(255,255,255,.4);letter-spacing:1px;text-transform:uppercase;margin-top:2px;}
.pl-no-rank{color:rgba(255,255,255,.45);font-size:13px;margin-top:14px;}
.pl-history-title{font-size:12px;letter-spacing:2px;color:rgba(255,215,0,.6);text-transform:uppercase;
  margin:26px 0 12px;text-align:center;}
.pl-sessions{display:flex;flex-direction:column;gap:9px;}
.pl-session{background:rgba(255,255,255,.035);border:1px solid rgba(255,215,0,.14);border-radius:12px;padding:12px 14px;}
.pl-session-top{display:flex;justify-content:space-between;align-items:center;gap:8px;}
.pl-session-time{font-size:13px;font-weight:700;}
.pl-session-pts{font-weight:800;color:#FFD700;font-size:14px;white-space:nowrap;}
.pl-session-meta{display:flex;gap:12px;margin-top:6px;font-size:11.5px;color:rgba(255,255,255,.5);flex-wrap:wrap;}
.pl-chip-live{display:inline-block;color:#ff6b6b;font-weight:700;}
.pl-empty-sessions{text-align:center;color:rgba(255,255,255,.35);font-size:13px;padding:20px 0;}
.pl-list-title{font-size:12px;letter-spacing:2px;color:rgba(255,215,0,.6);text-transform:uppercase;
  margin:30px 0 12px;text-align:center;}
.pl-list{display:flex;flex-direction:column;gap:8px;max-width:520px;margin:0 auto;}
.pl-list-row{display:flex;align-items:center;gap:12px;background:rgba(255,255,255,.03);
  border:1px solid rgba(255,215,0,.12);border-radius:12px;padding:10px 14px;cursor:pointer;
  transition:transform .12s ease,box-shadow .12s ease;}
.pl-list-row:active{transform:scale(.98);}
.pl-list-row.medal-1{background:linear-gradient(90deg,rgba(255,215,0,.18),rgba(255,215,0,.03));border-color:rgba(255,215,0,.55);}
.pl-list-row.medal-2{background:linear-gradient(90deg,rgba(200,200,216,.16),rgba(200,200,216,.03));border-color:rgba(200,200,216,.45);}
.pl-list-row.medal-3{background:linear-gradient(90deg,rgba(205,138,74,.16),rgba(205,138,74,.03));border-color:rgba(205,138,74,.45);}
.pl-list-row.selected{border-color:#4fd1c5;box-shadow:0 0 0 1px #4fd1c5,0 0 18px rgba(79,209,197,.35);
  background:rgba(79,209,197,.08);}
.pl-list-rank{width:34px;text-align:center;font-weight:800;font-size:15px;color:rgba(255,215,0,.8);flex:0 0 auto;}
.pl-list-info{flex:1;min-width:0;}
.pl-list-name{font-weight:700;font-size:13.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pl-list-live{color:#ff6b6b;font-size:10px;margin-left:6px;font-weight:700;}
.pl-list-sub{font-size:11px;color:rgba(255,255,255,.4);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pl-list-badge{font-size:9px;color:#4fd1c5;letter-spacing:1px;text-transform:uppercase;margin-top:2px;font-weight:700;}
.pl-list-pts{font-weight:800;color:#FFD700;font-size:13px;white-space:nowrap;flex:0 0 auto;text-align:right;}
.pl-list-empty{text-align:center;color:rgba(255,255,255,.35);font-size:13px;padding:24px 0;}
.pl-date-row{display:flex;justify-content:center;align-items:center;gap:10px;margin:-6px auto 22px;max-width:420px;flex-wrap:wrap;}
.pl-date-input{background:rgba(255,255,255,.04);border:1px solid rgba(255,215,0,.3);border-radius:10px;
  color:#fff;padding:8px 12px;font-family:inherit;font-size:13px;color-scheme:dark;}
.pl-date-today-btn{background:transparent;border:1px solid rgba(255,215,0,.4);color:#FFD700;border-radius:8px;
  padding:7px 12px;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;}
`;

export default function LeaderboardPlayerLookup({ variant = "contest" }) {
  const cfg = VARIANTS[variant];
  const [query, setQuery] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  const [rankings, setRankings] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayIST);
  const isToday = variant !== "daily" || selectedDate === todayIST();

  const loadRankings = useCallback(async () => {
    setListLoading(true);
    const url = variant === "daily" ? `${API_URL}${cfg.rankingsUrl}?date=${selectedDate}` : `${API_URL}${cfg.rankingsUrl}`;
    const res = await axiosRequest("GET", url, null);
    const payload = res?.data;
    if (payload) setRankings(payload.rankings || []);
    setListLoading(false);
  }, [variant, cfg.rankingsUrl, selectedDate]);

  useEffect(() => { loadRankings(); }, [loadRankings]);

  const selectPlayer = useCallback(async (q) => {
    const value = String(q || "").trim();
    if (!value) return;
    setDetailLoading(true);
    setError("");
    const dateParam = variant === "daily" ? `&date=${selectedDate}` : "";
    const res = await axiosRequest("GET", `${API_URL}${cfg.profileUrl}?query=${encodeURIComponent(value)}${dateParam}`, null);
    setDetailLoading(false);
    const payload = res?.data;
    if (!payload) return setError("Network error. Please try again.");
    if (!payload.found) return setError(payload.message || "No player found with that ID or phone number.");
    setResult(payload);
    setHighlightId(payload.player.playerId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [variant, cfg.profileUrl, selectedDate]);

  const changeDate = (d) => {
    setSelectedDate(d);
    setResult(null);
    setHighlightId(null);
    setError("");
  };

  const search = (e) => {
    e.preventDefault();
    selectPlayer(query);
  };

  // Computed on the true rank-sorted order, not orderedRankings (which reorders for
  // display), since it relies on comparing each player against their real neighbors.
  const displayDecimals = useMemo(() => computeDisplayDecimals(rankings), [rankings]);

  // Rank numbers come straight from the backend (tied players share a rank number);
  // the highlighted player is only moved to the front of the rendered order, not re-ranked.
  const orderedRankings = useMemo(() => {
    const idx = rankings.findIndex((r) => r._id === highlightId);
    if (idx <= 0) return rankings;
    const copy = [...rankings];
    const [pinned] = copy.splice(idx, 1);
    return [pinned, ...copy];
  }, [rankings, highlightId]);

  const rank = result?.rank;
  const stats = result?.stats;
  const isLive = stats?.is_live;
  const tiedWith = result?.tied_with || 1;

  // The highest-multiplier table the player has actually sat at, per their own session
  // history — suggesting a table they've never played (e.g. 500/1000/2000) isn't
  // actionable if they don't have access to it.
  const bestPlayedTable = useMemo(() => {
    if (!result?.sessions?.length) return null;
    let best = null;
    let bestMult = -1;
    for (const s of result.sessions) {
      const mult = TABLE_MULTIPLIERS[s.table_value] || 1;
      if (mult > bestMult) { bestMult = mult; best = s.table_value; }
    }
    return best;
  }, [result]);
  const fastestTableType = bestPlayedTable || "500/1000/2000";
  const fastestTableLabel = bestPlayedTable ? `your ${bestPlayedTable} table` : "a 500/1000/2000 table";

  return (
    <div className="pl-root">
      <style>{CSS}</style>
      <div className="pl-header">
        <div className="pl-club">Real Gaming Club</div>
        <div className="pl-title">{cfg.title}</div>
        <div className="pl-sub">Enter your Player ID or phone number</div>
      </div>

      <div className="pl-search-wrap">
        <form className="pl-search-box" onSubmit={search}>
          <input
            className="pl-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Player ID or phone number"
            inputMode="text"
            autoFocus
          />
          <button className="pl-btn" type="submit" disabled={detailLoading}>
            {detailLoading ? "..." : "Search"}
          </button>
        </form>
        <div className="pl-hint">Your rank updates live while you're checked in</div>
      </div>

      {variant === "daily" && (
        <div className="pl-date-row">
          <input
            type="date"
            className="pl-date-input"
            value={selectedDate}
            max={todayIST()}
            onChange={(e) => changeDate(e.target.value)}
          />
          {!isToday && (
            <button type="button" className="pl-date-today-btn" onClick={() => changeDate(todayIST())}>Today</button>
          )}
        </div>
      )}

      {error && <div className="pl-err">{error}</div>}

      {result && (
        <div className="pl-result">
          <div className={`pl-profile${isLive ? " live" : ""}`}>
            {variant === "milestone" ? (
              result.crowned ? <div className="pl-medal">👑</div> : rank ? <div className="pl-rank-plain">#{rank}</div> : null
            ) : rank && medalFor(rank) ? <div className="pl-medal">{medalFor(rank)}</div> : rank ? <div className="pl-rank-plain">#{rank}</div> : null}
            <div className="pl-name">{result.player.playerId}</div>
            {isLive && (
              <div className="pl-live-badge"><span className="pl-live-dot" />LIVE NOW</div>
            )}

            {stats ? (
              <>
                <div className="pl-pts">{fmtPts(stats.total_points)}</div>
                <div className="pl-pts-label">Points</div>
                {variant === "milestone" ? (
                  <>
                    {result.current_tier && (
                      <div className="pl-prize">{fmtPrize(result.current_tier.prize)} secured{result.crowned ? " — top tier!" : ""}</div>
                    )}
                    {result.next_tier && (
                      <div className="pl-next-tier">
                        {fmtPts(result.points_to_next, 0)} pts <b>or</b> {fmtHoursNeeded(result.hours_needed)} to <b>{fmtPrize(result.next_tier.prize)}</b>
                        {result.points_to_next > 0 && (
                          <div className="pl-tip">
                            💡 Fastest way: ~{fmtHoursNeeded(result.points_to_next / (TABLE_MULTIPLIERS[fastestTableType] * EARLY_SLOT_BONUS * 60))} on {fastestTableLabel} during the 6–11:59 AM bonus window (1.2×)
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  rank && prizeForRank(rank, variant, tiedWith) && (
                    <div className="pl-prize">
                      Currently winning {fmtPrize(prizeForRank(rank, variant, tiedWith))}
                      {tiedWith > 1 && ` (tied with ${tiedWith - 1} other${tiedWith > 2 ? "s" : ""})`}
                    </div>
                  )
                )}
                <div className="pl-stats">
                  <div className="pl-stat">
                    <div className="pl-stat-val">{rank || "—"}{result.total_players ? ` / ${result.total_players}` : ""}</div>
                    <div className="pl-stat-label">Rank</div>
                  </div>
                  <div className="pl-stat">
                    <div className="pl-stat-val">{fmtHrs(stats.total_minutes)}</div>
                    <div className="pl-stat-label">Hours</div>
                  </div>
                  <div className="pl-stat">
                    <div className="pl-stat-val">{stats.session_count}</div>
                    <div className="pl-stat-label">Sessions</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="pl-no-rank">{variant === "daily" && !isToday ? "No qualifying sessions that day." : cfg.emptyMsg}</div>
            )}
          </div>

          <div className="pl-history-title">Session History</div>
          {result.sessions && result.sessions.length ? (
            <div className="pl-sessions">
              {result.sessions.map((s) => {
                const { start, end } = clippedSessionWindow(s, result.period);
                return (
                  <div className="pl-session" key={s._id}>
                    <div className="pl-session-top">
                      <div className="pl-session-time">{fmtDateTime(start)} → {s.out_time ? fmtDateTime(end) : <span className="pl-chip-live">LIVE</span>}</div>
                      <div className="pl-session-pts">{fmtPts(variant === "milestone" ? sessionPointsMilestone(s, result.period) : sessionPoints(s, result.period))} pts</div>
                    </div>
                    <div className="pl-session-meta">
                      <span>{s.table_type}{s.table_value ? ` · ${s.table_value}` : ""}{s.table_number ? ` (Table ${s.table_number})` : ""}</span>
                      <span>{fmtHrs(sessionMinutes(s, result.period))} played</span>
                      {s.break_minutes > 0 && <span>Break: {s.break_minutes}m</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pl-empty-sessions">No sessions recorded yet.</div>
          )}
        </div>
      )}

      <div className="pl-list-title">
        {isIphoneVariant(variant)
          ? (isToday ? (variant === "daily" ? "Today's Standings" : "This Weekend's Standings") : `Standings — ${fmtDateShort(selectedDate)}`)
          : "All Players"}
      </div>
      {listLoading ? (
        <div className="pl-list-empty">Loading…</div>
      ) : !orderedRankings.length ? (
        <div className="pl-list-empty">No qualifying sessions yet.</div>
      ) : (
        <div className="pl-list">
          {orderedRankings.map((r) => (
            <div
              key={r._id}
              className={`pl-list-row${r.rank <= 3 ? ` medal-${r.rank}` : ""}${r._id === highlightId ? " selected" : ""}`}
              onClick={() => selectPlayer(r._id)}
            >
              <div className="pl-list-rank">
                {variant === "milestone" ? (r.crowned ? "👑" : `#${r.rank}`) : medalFor(r.rank) || `#${r.rank}`}
              </div>
              <div className="pl-list-info">
                <div className="pl-list-name">
                  {r._id}
                  {r.is_live && <span className="pl-list-live">● LIVE</span>}
                </div>
                <div className="pl-list-sub">
                  {fmtHrs(r.total_minutes)} · {r.session_count} session{r.session_count > 1 ? "s" : ""}
                  {variant === "milestone" && r.next_tier && ` · ${fmtPts(r.points_to_next, 0)} pts or ${fmtHoursNeeded(r.hours_needed)} to next`}
                </div>
                {r._id === highlightId && <div className="pl-list-badge">Viewing</div>}
              </div>
              <div className="pl-list-pts">
                {variant === "milestone"
                  ? r.current_tier ? fmtPrize(r.current_tier.prize) : `${fmtPts(r.total_points, displayDecimals[r._id])} pts`
                  : `${fmtPts(r.total_points, displayDecimals[r._id])} pts`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
